import { t as translate } from "../preferences";
import { KeyRound, LoaderCircle, LogOut } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError } from "../api";
import { useAuth } from "../auth";
import { LogoutConfirmation } from "../components/LogoutConfirmation";

export function ChangePasswordScreen({ preserveRoute = false }: { preserveRoute?: boolean }) {
  const { changePassword, logout } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [logoutPending, setLogoutPending] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (newPassword.length < 8) {
      setError(translate("Yangi parol kamida 8 ta belgidan iborat bo‘lishi kerak"));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(translate("Yangi parollar bir xil emas"));
      return;
    }
    setPending(true);
    setError(null);
    try {
      await changePassword(currentPassword, newPassword, confirmPassword);
      if (!preserveRoute) navigate("/mechanic/", { replace: true });
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : translate("Parolni almashtirib bo‘lmadi"),
      );
    } finally {
      setPending(false);
    }
  }

  async function handleLogout() {
    if (logoutPending) return;
    setLogoutPending(true);
    setError(null);
    try {
      await logout();
      navigate("/mechanic/login", { replace: true });
    } catch {
      setError(translate("Chiqishni tasdiqlab bo‘lmadi. Qayta urinib ko‘ring."));
    } finally {
      setLogoutPending(false);
    }
  }

  return (
    <main className="m-app-viewport m-app-safe-frame m-flex m-items-center m-justify-center m-bg-background m-px-4 m-py-8">
      <form className="m-w-full m-max-w-sm m-rounded-app m-border m-border-border m-bg-surface m-p-5 m-shadow-card" onSubmit={submit}>
        <span className="m-flex m-size-14 m-items-center m-justify-center m-rounded-2xl m-bg-elevated m-text-primary">
          <KeyRound className="m-size-7" aria-hidden="true" />
        </span>
        <h1 className="m-mt-5 m-text-2xl m-font-bold m-text-foreground">{translate("Yangi parol o‘rnating")}</h1>
        <p className="m-mt-2 m-text-sm m-leading-6 m-text-muted">
          {translate("Vaqtinchalik parolni almashtirgach ilovaga kirishingiz mumkin.")}</p>

        <label className="m-field-label m-mt-5" htmlFor="current-password">{translate("Joriy parol")}</label>
        <input id="current-password" className="m-text-input" type="password" autoComplete="current-password" value={currentPassword} disabled={pending} onChange={(event) => setCurrentPassword(event.target.value)} />

        <label className="m-field-label m-mt-4" htmlFor="new-password">{translate("Yangi parol")}</label>
        <input id="new-password" className="m-text-input" type="password" autoComplete="new-password" value={newPassword} disabled={pending} onChange={(event) => setNewPassword(event.target.value)} />

        <label className="m-field-label m-mt-4" htmlFor="confirm-password">{translate("Yangi parolni takrorlang")}</label>
        <input id="confirm-password" className="m-text-input" type="password" autoComplete="new-password" value={confirmPassword} disabled={pending} onChange={(event) => setConfirmPassword(event.target.value)} />

        {error ? <p className="m-error-box m-mt-4" role="alert">{error}</p> : null}

        <button className="m-primary-button m-mt-5" type="submit" disabled={pending || logoutPending}>
          {pending ? <LoaderCircle className="m-size-5 m-animate-spin" /> : <KeyRound className="m-size-5" />}
          {pending ? translate("Saqlanmoqda…") : translate("Parolni almashtirish")}
        </button>
        <button className="m-secondary-button m-mt-3" type="button" disabled={pending || logoutPending} onClick={() => setConfirmLogout(true)}>
          <LogOut className="m-size-5" aria-hidden="true" />
          {logoutPending ? translate("Chiqilmoqda…") : translate("Chiqish")}
        </button>
      </form>
      {confirmLogout ? <LogoutConfirmation onCancel={() => setConfirmLogout(false)} onConfirm={() => { setConfirmLogout(false); void handleLogout(); }} /> : null}
    </main>
  );
}
